import type { MemoSummary } from "@portal/schemas";

/**
 * メモの一覧の並べ替え・グループ・所有者での絞り込み(ユーザー要望 2026-10-15)。
 * 一覧はサーバーから更新の新しい順に最大 100 件来るので、その中で画面側で行う。
 */
export type MemoSort = "updated-desc" | "updated-asc" | "created-desc" | "title";
export type MemoGroup = "none" | "kind" | "owner" | "updated";

export const MEMO_SORT_LABELS: Record<MemoSort, string> = {
  "updated-desc": "更新が新しい順",
  "updated-asc": "更新が古い順",
  "created-desc": "作成が新しい順",
  title: "題名順",
};
export const MEMO_GROUP_LABELS: Record<MemoGroup, string> = {
  none: "グループなし",
  kind: "種類",
  owner: "所有者",
  updated: "更新日",
};

export interface MemoViewOptions {
  sort: MemoSort;
  group: MemoGroup;
  /** 所有者のメールで絞り込む。null ならすべて。 */
  owner: string | null;
}

export const DEFAULT_MEMO_VIEW: MemoViewOptions = {
  sort: "updated-desc",
  group: "none",
  owner: null,
};

export interface MemoGroupRows {
  /** グループの見出し。グループなしなら null。 */
  label: string | null;
  key: string;
  memos: MemoSummary[];
}

const titleOf = (m: MemoSummary) => m.title || "無題のメモ";
const collator = new Intl.Collator("ja");
const time = (iso: string) => new Date(iso).getTime();

export function sortMemos(memos: readonly MemoSummary[], sort: MemoSort): MemoSummary[] {
  const rows = [...memos];
  switch (sort) {
    case "updated-desc":
      return rows.sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
    case "updated-asc":
      return rows.sort((a, b) => time(a.updatedAt) - time(b.updatedAt));
    case "created-desc":
      return rows.sort((a, b) => time(b.createdAt) - time(a.createdAt));
    case "title":
      return rows.sort((a, b) => collator.compare(titleOf(a), titleOf(b)));
  }
}

/** 日本時間の日付(YYYY-MM-DD)。 */
const jstDay = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Tokyo" }).format(d);
const DAY = 86_400_000;

/** 更新日のグループ。今日・昨日・過去 7 日間・過去 30 日間・それ以前(日本時間の日付で数える)。 */
function updatedBucket(iso: string, now: Date): { key: string; label: string; order: number } {
  const days = Math.round(
    (new Date(jstDay(now)).getTime() - new Date(jstDay(new Date(iso))).getTime()) / DAY,
  );
  if (days <= 0) return { key: "today", label: "今日", order: 0 };
  if (days === 1) return { key: "yesterday", label: "昨日", order: 1 };
  if (days < 7) return { key: "week", label: "過去 7 日間", order: 2 };
  if (days < 30) return { key: "month", label: "過去 30 日間", order: 3 };
  return { key: "older", label: "それ以前", order: 4 };
}

const KIND = {
  mine: { label: "自分のメモ", order: 0 },
  sharing: { label: "共有中", order: 1 },
  shared: { label: "共有されたメモ", order: 2 },
} as const;

/**
 * 絞り込み → 並べ替え → グループ分け。グループの中は並べ替えの順。
 * グループの順: 種類は「自分のメモ・共有中・共有されたメモ」、所有者は自分が先でほかは名前順、
 * 更新日は新しい区切りから。
 */
export function arrangeMemos(
  memos: readonly MemoSummary[],
  options: MemoViewOptions,
  ctx: { me: string | null; nameOf: (email: string) => string | null; now: Date },
): MemoGroupRows[] {
  const filtered = options.owner ? memos.filter((m) => m.ownerEmail === options.owner) : memos;
  const sorted = sortMemos(filtered, options.sort);
  if (options.group === "none") return [{ label: null, key: "all", memos: sorted }];

  const groups = new Map<string, { label: string; order: number | string; memos: MemoSummary[] }>();
  const put = (key: string, label: string, order: number | string, m: MemoSummary) => {
    const g = groups.get(key) ?? { label, order, memos: [] };
    g.memos.push(m);
    groups.set(key, g);
  };
  for (const m of sorted) {
    if (options.group === "kind") {
      const k = m.role !== "OWNER" ? "shared" : m.shared ? "sharing" : "mine";
      put(k, KIND[k].label, KIND[k].order, m);
    } else if (options.group === "owner") {
      const mine = m.ownerEmail === ctx.me;
      const name = ctx.nameOf(m.ownerEmail) ?? m.ownerEmail;
      // 自分を先頭にし、ほかは名前の順
      put(m.ownerEmail, mine ? `自分(${name})` : name, mine ? "" : `1${name}`, m);
    } else {
      const b = updatedBucket(m.updatedAt, ctx.now);
      put(b.key, b.label, b.order, m);
    }
  }
  return [...groups.entries()]
    .sort(([, a], [, b]) =>
      typeof a.order === "number" && typeof b.order === "number"
        ? a.order - b.order
        : collator.compare(String(a.order), String(b.order)),
    )
    .map(([key, g]) => ({ key, label: g.label, memos: g.memos }));
}
